import React from 'react';
import { FaRegCalendarCheck, FaUmbrellaBeach, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { parseIso } from './isGunuHesap';
import { kapsamUyarisi } from './kesinlesmeHesaplayici';
import { REJIM_ETIKET } from './adliTatil';
import { birimAdi } from './ayarlar';
import { gunAdi, uzunTarih, kisaTarih, egikTarih } from './formatlayicilar';
import HesaplamaAdimlari from './HesaplamaAdimlari';

/** Tatil/uyarı bildirimi — amber (warn) tonlu sade şerit. */
function Uyari({ children }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-800 ring-1 ring-inset ring-amber-200/70">
      <span className="mt-0.5 text-amber-500">⚠</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * İstisnai süre durması bilgilendirme kartı (COVID / deprem).
 * Ulusal (COVID): düzeltilmiş tarihi de gösterir. Bölgesel (deprem): yalnızca iller + bilgi.
 */
function IstisnaKarti({ istisna }) {
  const ulusal = istisna.tur === 'ulusal';
  const ton = ulusal
    ? { kutu: 'bg-amber-50 text-amber-900 ring-amber-200/70', ikon: 'text-amber-500', alt: 'text-amber-800', kucuk: 'text-amber-700' }
    : { kutu: 'bg-rose-50 text-rose-900 ring-rose-200/70', ikon: 'text-rose-500', alt: 'text-rose-800', kucuk: 'text-rose-700' };
  return (
    <div className={`flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-sm leading-relaxed ring-1 ring-inset ${ton.kutu}`}>
      <FaInfoCircle className={`mt-0.5 h-3.5 w-3.5 flex-none ${ton.ikon}`} />
      <div>
        <div className="font-semibold">{istisna.ad} <span className="font-normal opacity-70">· {istisna.dayanak}</span></div>
        <p className={`mt-1 ${ton.alt}`}>{istisna.aciklama}</p>
        {ulusal && istisna.duzeltilmis ? (
          <p className="mt-2">
            Yukarıdaki hesap süre durmasını <strong>içermez</strong>. Durma dikkate alınırsa başvuru son günü{' '}
            <strong className="app-num">{kisaTarih(istisna.duzeltilmis.sonGun)}</strong>, kesinleşme{' '}
            <strong className="app-num">{kisaTarih(istisna.duzeltilmis.kesinlesme)}</strong> olur.
          </p>
        ) : null}
        {!ulusal && istisna.iller ? (
          <p className="mt-2"><span className="font-semibold">Etkilenen iller:</span> {istisna.iller.join(', ')}.</p>
        ) : null}
        <p className={`mt-2 text-xs ${ton.kucuk}`}>
          {ulusal
            ? 'Bilgilendirme amaçlıdır; somut olayda mevzuatla teyit ediniz.'
            : 'Bu dönem için tarih hesaplanmaz; durma yalnızca bu illerle bağlantılı kişi ve avukatları için geçerlidir. Somut durumu teyit ediniz.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Sonuç bloğu: başvuru son günü (sade satır) + kesinleşme (premium kahraman kart)
 * + adli tatil rozeti + hesaplama adımları + uyarılar + istisnai dönem kartları.
 * Türetimler sonuc'tan yapılır.
 */
function KanunYoluSonuc({ sonuc, tebligIso, sure, birim, ekBayramlar, istisnalar = [] }) {
  if (!sonuc) return null;

  const r = sonuc;
  const at = r.adliTatil || { rejim: 'kapali', uygulandi: false, tur: null };
  const adliTatilEtkin = at.rejim !== 'kapali';
  const istisnaVar = istisnalar.length > 0;
  // COVID gibi ulusal dönemde düzeltilmiş (alternatif) tarih kahraman kartında da gösterilir.
  const covidIstisna = istisnalar.find((i) => i.tur === 'ulusal' && i.duzeltilmis) || null;

  const sonGunNotu = birim === 'hafta'
    ? 'son haftada aynı isimli gün (m.92/m.39)'
    : birim === 'ay'
      ? (parseIso(r.hamSonGun).getDate() === parseIso(tebligIso).getDate()
          ? 'son ayda sayıca karşılık gelen gün'
          : 'karşılık gelen gün yok → ayın son günü')
      : gunAdi(r.hamSonGun);

  const adliStep = at.uygulandi
    ? {
        label: at.tur === 'baslangic'
          ? 'Adli tatil → süre 1 Eylül\'den işledi'
          : `Adli tatil → son gün taşındı (${at.rejim === 'cmk' ? 'CMK +3 gün' : 'HMK/İYUK +1 hafta'})`,
        val: kisaTarih(r.adliSonGun),
        sub: gunAdi(r.adliSonGun),
      }
    : null;

  const adimlar = [
    { label: 'Tebliğ tarihi', val: kisaTarih(tebligIso), sub: `${gunAdi(tebligIso)} · süreye dâhil değil` },
    { label: 'Sürenin başlangıcı', val: kisaTarih(r.baslangic), sub: `${gunAdi(r.baslangic)} · ertesi gün (1. gün)` },
    { label: `Sürenin son günü (+${sure} ${birimAdi(birim)})`, val: kisaTarih(r.hamSonGun), sub: sonGunNotu },
    ...(adliStep ? [adliStep] : []),
    ...(r.atlananlar.length
      ? [{ label: 'Son gün tatil → uzatıldı', val: kisaTarih(r.sonItiraz), sub: gunAdi(r.sonItiraz) }]
      : []),
    { label: 'Kanun yoluna başvurulabilecek son gün', val: kisaTarih(r.sonItiraz), sub: gunAdi(r.sonItiraz) },
    { label: 'Kesinleşme (son başvuru süresinin ertesi günü)', val: kisaTarih(r.kesinlesme), sub: gunAdi(r.kesinlesme) },
  ];

  const extNote = r.atlananlar.length
    ? `Son gün tatile rastladığı için süre ilk iş gününe uzatıldı: ${r.atlananlar
        .map((a) => `${kisaTarih(a.tarih)} — ${a.sebep}`)
        .join(' · ')}`
    : null;

  // Uyarılar
  const eksikYillar = kapsamUyarisi(tebligIso, r.kesinlesme, ekBayramlar);
  const kapsamMetin = eksikYillar.length
    ? eksikYillar.map((e) => `${e.yil} (${e.eksik.join(' ve ')})`).join('; ')
    : '';

  return (
    <div className="mt-6 animate-fade-in-up space-y-5">
      {/* Başvuru son günü — sade */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="app-eyebrow !tracking-[0.16em]">Kanun Yoluna Başvurulabilecek Son Gün</span>
        <span className="font-semibold text-slate-900">
          <span className="app-num">{uzunTarih(r.sonItiraz)}</span>
        </span>
        <span className="text-sm text-slate-400">{gunAdi(r.sonItiraz)}</span>
      </div>

      {/* Kesinleşme — premium kahraman kart */}
      <div className="app-panel-dark">
        <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-amber-300/50 bg-white/10 px-3 py-1 text-xs font-semibold text-amber-200">
          <FaRegCalendarCheck className="h-3 w-3" />
          <span className="app-num">{egikTarih(r.kesinlesme)}</span>
        </span>
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300/80">
          Kesinleşme tarihi
        </div>
        <div className="mt-2 font-display text-4xl font-semibold leading-none text-white sm:text-5xl">
          {uzunTarih(r.kesinlesme)}
        </div>
        <div className="mt-2.5 text-sm text-slate-300/90">{gunAdi(r.kesinlesme)}</div>

        {/* İstisnai dönem uyarısı + (COVID) alternatif tarih — kahraman kartı içinde */}
        {istisnaVar ? (
          <div className="mt-4 border-t border-white/10 pt-3.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-2.5 py-1 text-xs font-semibold text-amber-200 ring-1 ring-inset ring-amber-300/30">
              <FaExclamationTriangle className="h-3 w-3" />
              İstisnai süre durması dönemi
            </span>
            {covidIstisna ? (
              <div className="mt-2.5 text-sm leading-relaxed text-slate-300/90">
                Süre durması (COVID-19) dikkate alınırsa kesinleşme{' '}
                <strong className="app-num text-amber-100">{uzunTarih(covidIstisna.duzeltilmis.kesinlesme)}</strong>
                <span className="text-slate-400"> · başvuru son günü </span>
                <strong className="app-num text-amber-100">{kisaTarih(covidIstisna.duzeltilmis.sonGun)}</strong>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Adli tatil rozeti — kanun yolu süresi çipiyle aynı dilde */}
      {adliTatilEtkin ? (
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700">
            <FaUmbrellaBeach className="h-3 w-3" />
            Adli Tatil Etkin · {REJIM_ETIKET[at.rejim]}
          </span>
        </div>
      ) : null}

      {/* Hesaplama adımları */}
      <HesaplamaAdimlari adimlar={adimlar} extNote={extNote} />

      {/* Uyarılar */}
      {(kapsamMetin || at.uygulandi || r.atlananlar.length) ? (
        <div className="space-y-2.5">
          {at.uygulandi ? (
            <div className="flex items-start gap-2.5 rounded-xl bg-indigo-50 px-3.5 py-3 text-sm leading-relaxed text-indigo-800 ring-1 ring-inset ring-indigo-200/70">
              <FaUmbrellaBeach className="mt-0.5 h-3.5 w-3.5 flex-none text-indigo-500" />
              <span>
                {at.tur === 'baslangic' ? (
                  <>Süre başlangıcı adli tatile (20 Tem – 31 Ağu) rastladığından süre <strong>1 Eylül</strong>'den
                  itibaren işledi; son gün <strong className="app-num">{kisaTarih(r.adliSonGun)}</strong> oldu.</>
                ) : (
                  <>Sürenin son günü adli tatile (20 Tem – 31 Ağu) rastladığından <strong>{REJIM_ETIKET[at.rejim]}</strong>{' '}
                  kuralıyla <strong className="app-num">{kisaTarih(r.adliSonGun)}</strong> tarihine taşındı.</>
                )}
              </span>
            </div>
          ) : null}
          {kapsamMetin ? (
            <Uyari>
              Şu yıl için dinî bayram verisi eksik: <strong>{kapsamMetin}</strong>. Bu döneme denk gelecek bayram
              kanun yolu süresini etkileyebilir; eksik bayramı Ayarlar'dan ekleyin ya da sonucu resmî takvimle teyit edin.
            </Uyari>
          ) : null}
          {r.atlananlar.length ? (
            <Uyari>
              Son gün tatile rastladığından süre <strong className="app-num">{kisaTarih(r.adliSonGun)}</strong>{' '}
              yerine <strong className="app-num">{kisaTarih(r.sonItiraz)}</strong> tarihine uzadı (CMK m.39/4 · HMK m.93).
            </Uyari>
          ) : null}
        </div>
      ) : null}

      {/* İstisnai süre durması dönemleri (COVID / deprem) — yalnızca tarih örtüşürse */}
      {istisnalar.length ? (
        <div className="space-y-2.5">
          {istisnalar.map((i) => <IstisnaKarti key={i.id} istisna={i} />)}
        </div>
      ) : null}
    </div>
  );
}

export default KanunYoluSonuc;
